/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Remote Authentication Mode Utilities
 * 
 * Provides utilities for detecting and configuring remote authentication mode,
 * where authentication is handled by a remote master server instead of locally.
 */

import { getEnv, getEnvBoolean, isBrowser, isDevelopment, isServer, loadEnvironmentVariables } from './env';

/**
 * Check if remote authentication mode is enabled
 * 
 * Remote auth mode routes authentication requests (login, register, token refresh)
 * to a remote master server while all other operations use the local server.
 * 
 * This is independent of deployment type (Electron vs web) and is controlled
 * by the REMOTE_AUTH_MODE environment variable.
 * 
 * In Electron apps, the config is injected via window.__REMOTE_AUTH_CONFIG__
 * 
 * Uses the standardized getEnvBoolean() interface which handles all contexts.
 * 
 * @returns true if remote auth mode is enabled
 */
export function isRemoteAuthEnabled(): boolean {
  return getEnvBoolean('REMOTE_AUTH_MODE');
}

/**
 * Get remote authentication configuration
 * 
 * Uses the standardized getEnv() interface which handles all contexts.
 * 
 * @returns Object containing remote auth mode status and master server URL
 */
export function getRemoteAuthConfig(): {
  enabled: boolean;
  masterServerUrl: string | null;
} {
  const enabled = isRemoteAuthEnabled();
  
  if (!enabled) {
    return { enabled: false, masterServerUrl: null };
  }
  
  const masterServerUrl = getEnv('VELOCITY_MASTER_SERVER_URL') || null;
  
  return { enabled, masterServerUrl };
}

/**
 * Get the authentication mode description
 * 
 * Uses the standardized env interface which handles all contexts.
 * 
 * @returns A string describing the current authentication mode
 */
export function getAuthModeDescription(): string {
  // Check if auth is disabled (development mode)
  if (isDevelopment() && getEnvBoolean('DISABLE_AUTH')) {
    return 'disabled (development)';
  }
  
  // Check remote auth mode
  if (isRemoteAuthEnabled()) {
    return 'remote (master server)';
  }
  
  return 'local (default)';
}

